import {
  Component,
  effect,
  signal,
  inject,
  OnInit,
  computed,
  OnDestroy,
  Signal,
} from '@angular/core';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';

import { NavBar } from '../nav-bar/nav-bar';
import { ResponsiveService } from '../services/responsive';
import { AuthBridgeService } from '../services/auth-bridge';
import { ThemeService } from '../services/theme';
import { MobileWelcomeModal } from '../mobile-welcome-modal/mobile-welcome-modal';
import { MessageBridgeService } from '../services/message-bridge';
import { TimedOut } from '../timed-out/timed-out';
import { AuthStore } from '../stores/auth.store';
import { Loader } from '../loader/loader';
import { ActivatedRoute } from '@angular/router';
import { AnalyseBody } from '../analyse-body/analyse-body';
import { SnowplowService } from '../services/snowplow';
import { makeEvent } from '../analytics/builder';
import { InfonlineService } from '../services/infonline';
import { WorkflowAssetService } from '../services/api/store/assets';
import { AssetsStore } from '../stores/assets.store';
import { findAnalysisSignal, updatedAtSignal } from '../analysis-data';
import { findV3AnalysisSignal } from '../services/v3-guest-mode';
import { Background } from '../background/background';
import { PAGE_TITLE_V2_MAIN } from '../../constants/pageTitle';
import { PriceService } from '../services/price';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Quote } from '../services/push-quote';
import { PrevQuoteService } from '../services/prev-quote';
import { AppVersionService } from '../services/app-version';
import { FeatureFlagStore } from '../stores/feature-flags.store';
import { V2Disclaimer } from '../v2-disclaimer/v2-disclaimer';
import { TabNavigation } from '../tab-navigation/tab-navigation';
import { BackNavigationBar } from '../back-navigation-bar/back-navigation-bar';
import { CompetitorAnalysis } from '../competitor-analysis/competitor-analysis';
import { EarningsOverview } from '../earnings-overview/earnings-overview';
import { FundamentalScoresOverview } from '../fundamental-scores-overview/fundamental-scores-overview';
import { AnalystsOverview } from '../analysts-overview/analysts-overview';
import { NewsOverview } from '../news-overview/news-overview';
import { NewsAnalysisV2 } from '../news-analysis-v2/news-analysis-v2';
import { EarningsAnalysisV2 } from '../earnings-analysis-v2/earnings-analysis-v2';
import { AnalystsAnalysisV2 } from '../analysts-analysis-v2/analysts-analysis-v2';
import { FundamentalScoresAnalysisV2 } from '../fundamental-scores-analysis-v2/fundamental-scores-analysis-v2';
import { NavigationBridgeService } from '../services/navigation-bridge';
import { RegwallInfo } from '../regwall-info/regwall-info';
import { FinanzenHeader } from '../finanzen-header/finanzen-header';
import { SurveyBanner } from '../survey-banner/survey-banner';
import {
  FundamentalScoreSystemKey,
  hasRatedFundamentalScore,
  resolveFundamentalScoresUpdatedAt,
} from '../services/api/fundamental-scores';
import { logErrorPageShown } from '../services/api';
import { V3Root } from '../v3-root/v3-root';
import { V3CompanyProfile } from '../v3-company-profile/v3-company-profile';
import { V3Overview } from '../v3-overview/v3-overview';
import { V3FundamentalScoresAnalysis } from '../v3-fundamental-scores-analysis/v3-fundamental-scores-analysis';
import { V3NewsAnalysis } from '../v3-news-analysis/v3-news-analysis';
import { V3CompetitorAnalysis } from '../v3-competitor-analysis/v3-competitor-analysis';
import { V3AnalystsAnalysis } from '../v3-analysts-analysis/v3-analysts-analysis';
import { V3EarningsAnalysis } from '../v3-earnings-analysis/v3-earnings-analysis';
import { V3OnboardingModal } from '../v3-onboarding-modal/v3-onboarding-modal';
import { V3ChatWidget } from '../v3-chat-widget/v3-chat-widget';
import { V3LoadingScreen } from '../v3-loading-screen/v3-loading-screen';
import { V3InfoPage } from '../v3-info-page/v3-info-page';
import { hasV3InfoPage } from '../v3-info-page/v3-info-page-content';

const TAB_LABELS = [
  { label: 'Übersicht', id: 'main' },
  { label: 'News', id: 'news' },
  { label: 'Analysten', id: 'market_analyst_sentiment' },
  { label: 'Earnings', id: 'earnings' },
  { label: 'Fundamental Scores', id: 'fundamental_scores' },
  { label: 'Unternehmensprofil', id: 'action' },
];

// V3's tab nav — different order, plus Competitor as its own tab (V2 shows it as an
// inline main-page card, not a tab/subpage destination, so it stays out of TAB_LABELS).
const V3_TAB_LABELS = [
  { label: 'Übersicht', id: 'main' },
  { label: 'News', id: 'news' },
  { label: 'Earnings', id: 'earnings' },
  { label: 'Analysten', id: 'market_analyst_sentiment' },
  { label: 'Scores', id: 'fundamental_scores' },
  { label: 'Wettbewerb', id: 'competitive' },
  { label: 'Profil', id: 'action' },
];

// V3 Zero desktop subpage-card icon (V3ZeroSubpageCard's [icon]) — per-tab; every tab that
// falls through with no entry gets no icon (see Core.pageIcon). Analysten reuses Profil's
// icon rather than getting its own — no dedicated analysts icon was designed.
const V3_TAB_ICONS: Record<string, string> = {
  news: '/v3/news.svg',
  earnings: '/v3/earnings.svg',
  market_analyst_sentiment: '/v3/profile.svg',
  fundamental_scores: '/v3/scores.svg',
  competitive: '/v3/competitors.svg',
  action: '/v3/profile.svg',
};

@Component({
  selector: 'app-core',
  imports: [
    AnalyseBody,
    AsyncPipe,
    NgTemplateOutlet,
    Background,
    Loader,
    MobileWelcomeModal,
    NavBar,
    TimedOut,
    V2Disclaimer,
    TabNavigation,
    BackNavigationBar,
    CompetitorAnalysis,
    EarningsOverview,
    FundamentalScoresOverview,
    AnalystsOverview,
    NewsAnalysisV2,
    EarningsAnalysisV2,
    AnalystsAnalysisV2,
    FundamentalScoresAnalysisV2,
    NewsOverview,
    RegwallInfo,
    FinanzenHeader,
    SurveyBanner,
    V3Root,
    V3CompanyProfile,
    V3Overview,
    V3FundamentalScoresAnalysis,
    V3NewsAnalysis,
    V3CompetitorAnalysis,
    V3AnalystsAnalysis,
    V3EarningsAnalysis,
    V3OnboardingModal,
    V3LoadingScreen,
    V3InfoPage,
    V3ChatWidget,
  ],
  templateUrl: './core.html',
  styleUrl: './core.scss',
})
export class Core implements OnInit, OnDestroy {
  // Constants
  protected readonly title = signal('ai-frontend');

  // Services
  responsive = inject(ResponsiveService);
  private readonly snowplow = inject(SnowplowService);
  private readonly infonline = inject(InfonlineService);
  private readonly authBridge = inject(AuthBridgeService);
  private readonly themeService = inject(ThemeService);
  private readonly messageBridge = inject(MessageBridgeService);
  private readonly route = inject(ActivatedRoute);
  private readonly workflowService = inject(WorkflowAssetService);
  private readonly appVersion = inject(AppVersionService);
  private readonly navigationBridge = inject(NavigationBridgeService);
  private readonly featureFlags = inject(FeatureFlagStore);

  // Version
  public isV3 = this.appVersion.isV3;
  private readonly isV3Ready = this.appVersion.isV3Ready;
  private readonly versionSuffix = computed(() => (this.isV3() ? ' | V3' : ' | V2'));

  // Show the loader until: auth token is present AND Unleash has evaluated flags
  // with the real userId. This prevents a flash between the default experience and
  // V3 that occurs when a stale cached flag value renders the wrong one, then the
  // post-login Unleash re-evaluation corrects it. Guest mode bypasses this gate —
  // guests have no userId to wait for.
  // V3 also waits for the analyses response (guests included) — nav tabs and overview
  // cards derive from it, so rendering earlier flashes an empty shell (AI-611).
  // V3 also waits for V3LoadingScreen's own ~2.5s minimum-duration timer (AI-614).
  readonly showLoader = computed(() => {
    if (this.timedOut()) return false;
    const v3AwaitingData = this.isV3() && !this.assetsStore.v2Data() && !this.assetsStore.error();
    const v3AwaitingLoadingScreen = this.isV3() && !this.v3LoadingScreenElapsed();
    if (this.isGuestMode()) return v3AwaitingData || v3AwaitingLoadingScreen;
    return !this.auth.token() || !this.isV3Ready() || v3AwaitingData || v3AwaitingLoadingScreen;
  });
  // Per-Core-instance, so a routerLink round trip to /privacy·/terms replays the screen too.
  private readonly v3LoadingScreenElapsed = signal(false);
  onV3LoadingScreenElapsed(): void {
    this.v3LoadingScreenElapsed.set(true);
  }
  // True once the real content tree (not the loader or the error page) is showing —
  // drives the `content-ready` body class the V3 footer accent is CSS-gated on.
  readonly contentReady = computed(() => !this.showLoader() && !this.timedOut() && !!this.isin());
  public isFeedbackEnabled = this.featureFlags.isFeedbackEnabled;
  public isTraderfoxEnabled = this.featureFlags.isTraderfoxEnabled;
  public isFinnet = this.themeService.isFinnet;
  public isFinnetApp = this.themeService.isFinnetApp;
  /**
   * True when the Finnet parent has resolved auth (AUTH_TOKEN received) but
   * delivered a null token — meaning the user is unauthenticated (guest).
   * The regwall is shown in this state. Data is loaded via the public portal
   * endpoint (no auth required).
   */
  public isGuestMode = computed(
    () =>
      this.isFinnet() &&
      this.authBridge.authResolved() &&
      !this.authBridge.token() &&
      !this.authBridge.tokenError(),
  );
  private readonly canLoad = computed(() => !!this.auth.token() || this.isGuestMode());
  readonly safeAreaTop = signal(0);
  readonly showMobileFinanzenHeader = computed(
    () => !this.isFinnet() && !this.isV3() && this.responsive.isMobile(),
  );
  readonly priceService = inject(PriceService);
  readonly prevQuoteService = inject(PrevQuoteService);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  price$!: Observable<Record<string, any>>;
  /** Live bid price (EUR) per ISIN, derived from price$ — feeds the Competitor
   * table so its "Potential" can be recomputed per row like the Analysten tab. */
  livePrices$!: Observable<Record<string, number>>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  prevQuote$!: Observable<Record<string, any>>;

  // 'main' | 'analyse' | 'info' — 'info' is a tab's info page (V3 only, AI-637), with
  // analyseType still naming the tab so the nav keeps it active.
  section = signal<string>('main');
  analyseType = signal<string>('');
  isin = signal<string | null>(null);
  // Set when V3ScoresOverview's navigate fires from a specific tile (not a generic header/
  // "Details" click) — lets V3FundamentalScoresAnalysis open with that category active.
  readonly selectedScoreCategory = signal<FundamentalScoreSystemKey | null>(null);
  readonly priceIsin = computed(() => this.assetsStore.v2Data()?.isin ?? null);

  /** Store */
  public auth = inject(AuthStore);

  timedOut = computed(() => this.authBridge.tokenError() || this.auth.refreshFailed());

  activeTab = computed(() => {
    if (this.section() === 'main') return 'main';
    return this.analyseType() || 'main';
  });
  private readonly assetsStore = inject(AssetsStore);
  readonly loading = this.assetsStore.loading;
  private readonly activeData = computed(() => this.assetsStore.v2Data());

  readonly competitionAnalysis = this.findAnalysis('competition');
  // Subscribe to the subject and up to 20 peers for the competition detail page.
  // The overview displays only the first four, but both views use live prices.
  private readonly competitorIsins = computed(() => {
    const content = this.competitionAnalysis()?.content;
    const isins = new Set<string>();
    const subjectIsin = content?.['subject']?.['isin'];
    if (subjectIsin) isins.add(subjectIsin);
    (content?.['peers'] ?? []).slice(0, 20).forEach((peer: { isin?: string }) => {
      if (peer?.isin) isins.add(peer.isin);
    });
    return Array.from(isins);
  });
  readonly newsAnalysis = this.findAnalysis('news');
  readonly analystsAnalysis = this.findAnalysis('analysts_analysis');
  readonly earningsAnalysis = this.findAnalysis('earnings_report');
  readonly earningsTranscriptsAnalysis = this.findAnalysis('earnings_transcripts');
  readonly companyAnalysis = this.findAnalysis('general');
  // Not part of AssetResponse.analyses[] — fetched separately via
  // WorkflowAssetService.loadFundamentalScores into AssetsStore.fundamentalScores.
  readonly fundamentalScoresRaw = computed(() => this.assetsStore.fundamentalScores());

  // False when every system TraderFox sent back is suspended (e.g. "insufficient data"
  // across the board) — there's nothing meaningful to show, so the tab/card are hidden
  // entirely rather than rendering an empty-looking "0 von 0" state.
  readonly hasFundamentalScoresRating = computed(() => {
    const raw = this.fundamentalScoresRaw();
    return !!raw && hasRatedFundamentalScore(raw);
  });

  private isTabVisible(id: string): boolean {
    if (id === 'news') return !!this.newsAnalysis();
    if (id === 'market_analyst_sentiment') return !!this.analystsAnalysis();
    if (id === 'earnings') return !!(this.earningsAnalysis() || this.earningsTranscriptsAnalysis());
    if (id === 'fundamental_scores')
      return this.isTraderfoxEnabled() && (this.isGuestMode() || this.hasFundamentalScoresRating());
    if (id === 'action') return !!this.companyAnalysis();
    if (id === 'competitive') return this.isGuestMode() || !!this.competitionAnalysis();
    return true;
  }

  readonly visibleTabs = computed(() => TAB_LABELS.filter((tab) => this.isTabVisible(tab.id)));

  readonly v3VisibleTabs = computed(() => V3_TAB_LABELS.filter((tab) => this.isTabVisible(tab.id)));

  private findAnalysis(type: string) {
    return findAnalysisSignal(this.assetsStore.v2Data, type);
  }

  stockName = computed(() => {
    const data = this.activeData();
    return data?.name || data?.isin || '';
  });

  readonly stockLogo = computed(() => this.activeData()?.iconUrl ?? null);

  // V3 subpage header title — looked up against V3_TAB_LABELS (a superset of TAB_LABELS,
  // e.g. it also has Wettbewerb) rather than the filtered visibleTabs()/v3VisibleTabs(),
  // since the active tab is always one the user was able to navigate to.
  readonly activeTabTitle = computed(
    () => V3_TAB_LABELS.find((tab) => tab.id === this.activeTab())?.label ?? '',
  );

  // V3 Zero desktop subpage-card icon for the active tab — see V3_TAB_ICONS above.
  readonly pageIcon = computed(() => V3_TAB_ICONS[this.activeTab()] ?? null);

  // V3 subpage timestamp (mobile analysis header, desktop subpage cards): what the tab's overview
  // card shows (guest mocks included), so authenticated Scores and Profil get none.
  private readonly v3TabUpdatedAt: Record<string, Signal<Date | null>> = {
    news: this.v3AnalysisUpdatedAt('news'),
    earnings: this.v3AnalysisUpdatedAt('earnings_report'),
    market_analyst_sentiment: this.v3AnalysisUpdatedAt('analysts_analysis'),
    competitive: this.v3AnalysisUpdatedAt('competition'),
    fundamental_scores: computed(() => resolveFundamentalScoresUpdatedAt(this.isGuestMode())),
  };
  readonly subpageUpdatedAt = computed(() => this.v3TabUpdatedAt[this.activeTab()]?.() ?? null);

  private v3AnalysisUpdatedAt(type: string): Signal<Date | null> {
    return updatedAtSignal(findV3AnalysisSignal(this.assetsStore.v2Data, type, this.isGuestMode));
  }

  private trackMainPageView() {
    this.snowplow.pageView(PAGE_TITLE_V2_MAIN + this.versionSuffix(), 'overview');
  }

  onTabChange(tabId: string) {
    if (tabId === 'main') {
      this.backToMain();
    } else {
      // A direct tab click is a generic entry, same as a header click on the overview card —
      // reset so a stale category from a previous tile click doesn't leak in.
      this.selectedScoreCategory.set(null);
      this.setAnalyse(tabId);
    }
  }

  // V3ScoresOverview's navigate — key is the clicked tile's system, or undefined for a
  // generic header/"Details" click (resets to the sub-page's default first category).
  onScoresNavigate(key?: FundamentalScoreSystemKey) {
    this.selectedScoreCategory.set(key ?? null);
    this.setAnalyse('fundamental_scores');
  }

  /** Other helpers */
  setAnalyse(type: string) {
    // Tab bars re-emit tabChange for an already-active tab, so a repeat click must not count
    // as a new IVW impression.
    const sectionChanged = this.section() !== 'analyse' || this.analyseType() !== type;
    this.section.set('analyse');
    this.analyseType.set(type);

    const v2Titles: Record<string, string> = {
      news: 'News | AI Analyse | finanzen.net zero',
      market_analyst_sentiment: 'Analysten | AI Analyse | finanzen.net zero',
      earnings: 'Earnings | AI Analyse | finanzen.net zero',
      action: 'Unternehmen | AI Analyse | finanzen.net zero',
      fundamental_scores: 'Fundamental Scores | AI Analyse | finanzen.net zero',
      competitive: 'Wettbewerb | AI Analyse | finanzen.net zero',
    };
    const title = v2Titles[type];
    if (title) this.snowplow.pageView(title + this.versionSuffix(), type);
    if (sectionChanged) this.infonline.pageview();
    this.scrollToTop();
  }

  backToMain() {
    // Same repeat-click guard as setAnalyse().
    const sectionChanged = this.section() !== 'main';
    this.section.set('main');
    this.analyseType.set('');

    this.trackMainPageView();
    // Not in trackMainPageView() itself — that also runs from the initial-load effect below,
    // which fires infonline.pageview() unconditionally (safe: guarded by initialViewTracked).
    if (sectionChanged) this.infonline.pageview();
  }

  /** Helper for the new header */
  goToCompanyAnalysis() {
    this.setAnalyse('action');
  }

  // V3: the per-tab info icon — analysis subpages whose tab has an article (Profil has none yet).
  readonly showInfoButton = computed(
    () => this.section() === 'analyse' && hasV3InfoPage(this.analyseType()),
  );

  // V3: guests read a tab's info page in full — no regwall card over it (styles.scss keeps it sharp).
  readonly showRegwall = computed(() => this.isGuestMode() && this.section() !== 'info');

  // V3: that icon → the tab's info page.
  openInfoPage() {
    if (!this.showInfoButton()) return;
    this.section.set('info');

    const title = this.activeTabTitle();
    if (title)
      this.snowplow.pageView(
        `${title} Info | AI Analyse | finanzen.net zero${this.versionSuffix()}`,
        `${this.analyseType()}/info`,
      );
    this.infonline.pageview();
    this.scrollToTop();
  }

  // V3 back button: info page → its tab's subpage, subpage → overview.
  onSubpageBack() {
    if (this.section() === 'info') this.setAnalyse(this.analyseType());
    else this.backToMain();
  }

  private scrollToTop() {
    this.messageBridge.postMessage('SCROLL_TOP');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }, 10);
  }

  /** Responsive + Auth bridge + load data */
  constructor() {
    // Keep AuthStore in sync with the bridge token.
    // The else-if clears the store when the bridge has resolved auth with a null token
    // (Finnet guest or mid-session logout), preventing a stale JWT from keeping
    // canLoad true. The authResolved() guard is intentional: before AUTH_TOKEN is
    // received, bridge.token() is null but the store may hold a token set via the
    // ?token= URL param (E2E / QA path). Clearing unconditionally would wipe that out.
    effect(() => {
      const newToken = this.authBridge.token();
      const newAccessToken = this.authBridge.accessToken();
      const newUsername = this.authBridge.username();
      if (newToken) {
        this.auth.setToken(newToken);
      } else if (this.authBridge.authResolved()) {
        this.auth.clearToken();
      }
      if (newUsername) {
        this.auth.setUsername(newUsername);
      }
      if (newAccessToken) {
        this.auth.setAccessToken(newAccessToken);
      }
    });

    effect(() => {
      document.body.classList.toggle('guest-mode', this.isGuestMode());
    });

    // onCleanup: routerLink navigation away from Core (e.g. to /privacy or /terms, both
    // reachable via links inside Core's own modals) destroys Core without a full page
    // reload, which would otherwise strand v3-mode on body — and with it V3's global
    // background/font/footer rules — on a route that never mounts V3Root to correct it.
    effect((onCleanup) => {
      document.body.classList.toggle('v3-mode', this.isV3());
      onCleanup(() => document.body.classList.remove('v3-mode'));
    });

    effect((onCleanup) => {
      document.body.classList.toggle('content-ready', this.contentReady());
      onCleanup(() => document.body.classList.remove('content-ready'));
    });

    // Fire the initial page view once the version is settled. Guest mode bypasses
    // the isV3Ready gate (guests have no userId for Unleash to re-evaluate), so
    // either condition unblocks tracking — mirroring showLoader's auth gate (not its
    // V3 data wait: the impression counts the visit, not first paint).
    //
    // Also the only place the IVW initial impression fires (index.html only sets up the
    // loader/queue now) — this effect runs on every Core mount, so a routerLink round trip to
    // /privacy or /terms and back (see the v3-mode cleanup effect above) still gets its own
    // impression, unlike a one-shot script tied to the first document load.
    let initialViewTracked = false;
    effect(() => {
      if ((this.isV3Ready() || this.isGuestMode()) && !initialViewTracked) {
        initialViewTracked = true;
        this.snowplow.trackerReady().then(() => this.trackMainPageView());
        this.infonline.pageview();
      }
    });

    // Report the error page (app-timed-out) to the backend so it can be counted and
    // alerted on in CloudWatch. One-shot per session: a flapping signal (e.g. a later
    // retry cycle re-setting refreshFailed) must not spam the endpoint.
    let errorPageReported = false;
    effect(() => {
      if (this.timedOut() && !errorPageReported) {
        errorPageReported = true;
        logErrorPageShown({
          reason: this.authBridge.tokenError() ? 'token_error' : 'refresh_failed',
          isin: this.isin(),
          theme: this.themeService.theme(),
        });
      }
    });

    // FETCH & STORE
    // Assets: fetch on isin change. Guest mode uses the public portal endpoint.
    effect(() => {
      const assetIsin = this.isin();
      if (assetIsin && this.canLoad()) {
        this.workflowService.load(assetIsin);
      }
    });

    // Fundamental Scores: separate endpoint, authenticated users only — guests always
    // see the mock (see FundamentalScoresOverview/FundamentalScoresAnalysisV2).
    effect(() => {
      const assetIsin = this.isin();
      if (assetIsin && this.canLoad() && this.isTraderfoxEnabled() && !this.isGuestMode()) {
        this.workflowService.loadFundamentalScores(assetIsin);
      }
    });

    // PRICES
    // For derivatives, v2Data.isin is the parent ISIN; null until v2Data loads.
    effect(() => {
      const priceIsin = this.priceIsin();
      const competitorIsins = this.competitorIsins();
      this.priceService.unsubscribeFromQuotes();

      if (priceIsin && this.canLoad()) {
        const isins = Array.from(new Set([priceIsin, ...competitorIsins]));
        this.price$ = this.priceService.subscribeToQuotesBuffered(isins);
        this.livePrices$ = this.price$.pipe(map((quotes) => this.toBidMap(quotes)));
        this.prevQuote$ = this.prevQuoteService.getPrevQuotes([priceIsin]);
      }
    });
  }

  private toBidMap(quotes: Record<string, Quote>): Record<string, number> {
    const bids: Record<string, number> = {};
    Object.entries(quotes ?? {}).forEach(([isin, quote]) => {
      if (quote?.bid != null) bids[isin] = quote.bid;
    });
    return bids;
  }

  parentGoBack() {
    this.messageBridge.postMessage('GO_BACK');
  }

  // Competitor row click (V2 and V3 tables). Opening the other stock leaves this page, so the
  // hand-off waits until the click event is out.
  navigateToInstrument(isin: string) {
    void this.snowplow
      .trackBeforeLeaving(
        makeEvent('element_clicked', {
          element_title: 'competitor_link',
          feature_name: 'link',
          property: { isin },
        }),
      )
      .then(() => this.navigationBridge.navigateToInstrument(isin));
  }

  ngOnInit() {
    this.themeService.init();

    this.route.queryParamMap.subscribe((params) => {
      // Token
      const tokenParam = params.get('token');
      const currentToken = this.auth.token();

      if (tokenParam && !currentToken) {
        this.auth.setToken(tokenParam);
      }

      // Is about special isin
      const givenIsin = params.get('isin');
      this.isin.set(givenIsin);

      this.safeAreaTop.set(Number(params.get('safeAreaTop') || 0));
    });
  }

  ngOnDestroy(): void {
    this.priceService.unsubscribeFromQuotes();
  }
}
